import React, { useState, useEffect, useRef } from 'react';
import { CodeRain, FlyingBat, CastleSilhouette } from './shared/Effects';
import { Link } from 'react-router-dom';

/* ═══════════════════════════════════════════
   SCROLL ANIMATION HOOK
   ═══════════════════════════════════════════ */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

/* ═══════════════════════════════════════════
   ANIMATED SECTION WRAPPER
   ═══════════════════════════════════════════ */
const AnimatedSection = ({ children, delay = 0, style = {} }) => {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} style={{
      ...style,
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN PRESENTATION PAGE
   ═══════════════════════════════════════════ */
const PresentationPage = ({ onSwitchToKids, onOpenForm }) => {
  const [visible, setVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredPrice, setHoveredPrice] = useState(null);
  const fullText = 'Construim experiențe digitale care nu mor niciodată.';

  useEffect(() => {
    setVisible(true);
    let i = 0;
    const timer = setInterval(() => {
      if (i <= fullText.length) {
        setTypedText(fullText.slice(0, i));
        i++;
      } else clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const services = [
    { icon: '🏰', title: 'Website-uri', desc: 'De la landing page la e-commerce. Fiecare site e o fortăreață digitală.', slug: 'website-uri', color: '#50fa7b' },
    { icon: '⚡', title: 'Aplicații Web', desc: 'React, Django, Flask — stiva completă, putere completă.', slug: 'aplicatii-web', color: '#ff79c6' },
    { icon: '🎨', title: 'Design UI/UX', desc: 'Interfețe care captivează. Dark mode? Întotdeauna.', slug: 'design-ui-ux', color: '#bd93f9' },
    { icon: '🔧', title: 'Mentenanță', desc: 'Site-ul tău nu doarme niciodată. Nici noi.', slug: 'mentenanta', color: '#f1fa8c' },
  ];

  const steps = [
    { num: '01', icon: '📝', title: 'Descrii afacerea', desc: 'Completezi un formular simplu cu detalii despre business-ul tău. Durează 2 minute.', color: '#50fa7b' },
    { num: '02', icon: '🧄', title: 'AI-ul generează 3 demo-uri', desc: 'În 30 de secunde primești 3 variante de website, fiecare cu stil diferit.', color: '#ff79c6' },
    { num: '03', icon: '🚀', title: 'Alegi și lansăm', desc: 'Selectezi varianta preferată, o personalizăm împreună și lansăm site-ul.', color: '#bd93f9' },
  ];

  const trustPoints = [
    { icon: '💰', text: 'Prețuri accesibile pentru România' },
    { icon: '✍️', text: 'Cod scris manual, nu template WordPress' },
    { icon: '🤖', text: 'AI integrat în procesul de dezvoltare' },
    { icon: '🇷🇴', text: 'Suport în română, comunicare directă' },
    { icon: '⚡', text: 'Livrare rapidă: 2-4 săptămâni' },
    { icon: '🔒', text: 'SSL gratuit, hosting securizat' },
  ];

  const prices = [
    { title: 'Landing Page', price: '500', unit: 'RON', desc: 'O pagină, impact maxim', icon: '🎯', color: '#50fa7b', popular: false },
    { title: 'Site Prezentare', price: '800', unit: 'RON', desc: 'Multi-pagină profesional', icon: '📄', color: '#8be9fd', popular: true },
    { title: 'Magazin Online', price: '2.000', unit: 'RON', desc: 'E-commerce complet', icon: '🛒', color: '#ff79c6', popular: false },
    { title: 'Aplicație Custom', price: '3.000', unit: 'RON', desc: 'Full-stack pe măsură', icon: '🚀', color: '#bd93f9', popular: false },
    { title: 'Mentenanță', price: '100', unit: 'RON/lună', desc: 'Grijă continuă', icon: '🛡️', color: '#f1fa8c', popular: false },
  ];

  const sectionPadding = { padding: '100px 24px', position: 'relative', zIndex: 10 };
  const sectionInner = { maxWidth: 1000, margin: '0 auto' };
  const sectionTitle = {
    textAlign: 'center', fontSize: 'clamp(24px, 4vw, 32px)', marginBottom: 16,
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
  };
  const sectionSubtitle = {
    textAlign: 'center', color: '#6272a4', fontSize: 15,
    maxWidth: 550, margin: '0 auto 56px', lineHeight: 1.6,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      color: '#f8f8f2',
      fontFamily: "'Fira Code', monospace",
      position: 'relative',
      overflow: 'hidden',
    }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(189,147,249,0.1); }
          50% { box-shadow: 0 0 40px rgba(189,147,249,0.25); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes gradientText {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @media (max-width: 768px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .prices-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .trust-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .prices-grid { grid-template-columns: 1fr !important; }
        }
          @media (max-width: 600px) {
  nav { padding: 10px 16px !important; }
  nav button { font-size: 10px !important; padding: 5px 10px !important; }
}
      `}</style>

      <CodeRain />
      <FlyingBat delay={1000} startX={-50} startY={80} size={20} />
      <FlyingBat delay={4000} startX={-80} startY={200} size={16} />
      <FlyingBat delay={7000} startX={-30} startY={350} size={22} />

      {/* Dark overlay over background image */}
      <div style={{
  position: 'absolute', inset: 0, zIndex: 1,
  background: 'rgba(13,14,18,0.85)',
  pointerEvents: 'none',
}} />

      {/* ═══ NAV ═══ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 32px',
        background: 'rgba(13,14,18,0.85)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(68,71,90,0.3)',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <span style={{ fontSize: 26 }}>🧛</span>
          <span style={{
            fontSize: 19, fontWeight: 700,
            background: 'linear-gradient(135deg, #bd93f9, #ff79c6)',
            backgroundSize: '200% 200%',
            animation: 'gradientText 4s ease infinite',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>DraculaDev</span>
          <span style={{ color: '#50fa7b', fontSize: 11, opacity: 0.6, fontFamily: "'JetBrains Mono', monospace" }}>.com</span>
        </Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={onOpenForm} style={{
            background: 'transparent', border: '1px solid #50fa7b40',
            borderRadius: 8, padding: '7px 16px',
            color: '#50fa7b', fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 500, fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.target.style.background = '#50fa7b15'; e.target.style.borderColor = '#50fa7b'; }}
          onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.borderColor = '#50fa7b40'; }}>
            Demo Gratuit
          </button>
          <button onClick={onSwitchToKids} style={{
            background: 'linear-gradient(135deg, #ff79c6, #bd93f9)',
            border: 'none', borderRadius: 8, padding: '8px 18px',
            color: '#fff', fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}>
            🎮 Cursuri Copii
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        padding: '120px 24px 80px', textAlign: 'center',
        position: 'relative', zIndex: 10,
        background: `radial-gradient(ellipse at 50% 0%, rgba(189,147,249,0.1) 0%, transparent 60%),
                     radial-gradient(ellipse at 20% 80%, rgba(80,250,123,0.06) 0%, transparent 40%)`,
        opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 1s ease',
      }}>
        <div style={{
          background: '#16181e', borderRadius: 14, maxWidth: 680, width: '100%',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(189,147,249,0.08)',
          overflow: 'hidden', marginBottom: 48, border: '1px solid #1e2030',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 16px', background: '#111218', borderBottom: '1px solid #1e2030',
          }}>
            <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5555' }} />
            <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#f1fa8c' }} />
            <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#50fa7b' }} />
            <span style={{ marginLeft: 12, color: '#44475a', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>dracula@dev ~ </span>
          </div>
          <div style={{ padding: '24px 24px 28px', fontSize: 13, lineHeight: 2, fontFamily: "'JetBrains Mono', monospace" }}>
            <div><span style={{ color: '#50fa7b' }}>$</span> <span style={{ color: '#8be9fd' }}>whoami</span></div>
            <div style={{ color: '#f8f8f2', margin: '2px 0 10px' }}>DraculaDev — Web Development Studio</div>
            <div><span style={{ color: '#50fa7b' }}>$</span> <span style={{ color: '#8be9fd' }}>cat</span> <span style={{ color: '#f1fa8c' }}>mission.txt</span></div>
            <div style={{ color: '#ff79c6', margin: '2px 0 10px', minHeight: '1.8em' }}>
              {typedText}<span style={{ animation: 'blink 1s step-end infinite', color: '#f8f8f2' }}>▊</span>
            </div>
            <div><span style={{ color: '#50fa7b' }}>$</span> <span style={{ color: '#8be9fd' }}>ls</span> <span style={{ color: '#f1fa8c' }}>services/</span></div>
            <div style={{ color: '#bd93f9' }}>websites/  apps/  design/  maintenance/</div>
          </div>
        </div>

        <h1 style={{
          fontSize: 'clamp(34px, 5.5vw, 60px)', fontWeight: 800,
          lineHeight: 1.08, marginBottom: 20,
          fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em',
        }}>
          <span style={{ color: '#f8f8f2' }}>Transformăm idei în </span><br />
          <span style={{
            background: 'linear-gradient(135deg, #bd93f9, #ff79c6, #50fa7b, #8be9fd)',
            backgroundSize: '300% 300%', animation: 'gradientText 6s ease infinite',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>cod nemuritor</span>
        </h1>

        <p style={{
          color: '#6272a4', fontSize: 16, maxWidth: 480, lineHeight: 1.7, marginBottom: 40,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          Web development din inima Transilvaniei. Site-uri, aplicații web și soluții digitale care lucrează non-stop.
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={onOpenForm} style={{
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)',
            border: 'none', borderRadius: 10, padding: '15px 36px',
            color: '#0d0e12', fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
            boxShadow: '0 0 30px rgba(80,250,123,0.25)', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 40px rgba(80,250,123,0.35)'; }}
          onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 0 30px rgba(80,250,123,0.25)'; }}>
            ⚡ Descrie afacerea ta
          </button>
          <a href="#preturi" style={{
            padding: '15px 28px', borderRadius: 10, border: '1px solid #44475a',
            background: 'transparent', color: '#a0a4b8',
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 14,
            cursor: 'pointer', textDecoration: 'none', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.target.style.borderColor = '#bd93f9'; e.target.style.color = '#f8f8f2'; }}
          onMouseLeave={e => { e.target.style.borderColor = '#44475a'; e.target.style.color = '#a0a4b8'; }}>
            Vezi prețuri ↓
          </a>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section style={{ ...sectionPadding, background: 'linear-gradient(180deg, transparent, rgba(189,147,249,0.02), transparent)' }}>
        <div style={sectionInner}>
          <AnimatedSection>
            <h2 style={sectionTitle}>
              <span style={{ color: '#44475a' }}>// </span>
              <span style={{ color: '#ff79c6' }}>Ce putem face pentru tine</span>
            </h2>
            <p style={sectionSubtitle}>De la landing page la sisteme enterprise — tot ce ai nevoie pentru prezența ta digitală.</p>
          </AnimatedSection>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
            {services.map((s, i) => (
              <AnimatedSection key={i} delay={0.1 * i}>
                <Link to={`/servicii/${s.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    onMouseEnter={() => setHoveredService(i)}
                    onMouseLeave={() => setHoveredService(null)}
                    style={{
                      background: hoveredService === i ? `${s.color}08` : '#13141a',
                      border: `1px solid ${hoveredService === i ? s.color + '40' : '#1e2030'}`,
                      borderRadius: 14, padding: '28px 24px', cursor: 'pointer', height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      transform: hoveredService === i ? 'translateY(-6px)' : 'none',
                      boxShadow: hoveredService === i ? `0 12px 40px ${s.color}12` : 'none',
                    }}>
                    <div style={{ fontSize: 34, marginBottom: 16 }}>{s.icon}</div>
                    <h3 style={{ color: s.color, fontSize: 17, marginBottom: 10, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>{s.title}</h3>
                    <p style={{ color: '#6272a4', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>{s.desc}</p>
                    <span style={{ color: s.color, fontSize: 12, opacity: 0.5, fontFamily: "'JetBrains Mono', monospace", transition: 'all 0.2s' }}>
                      vezi detalii →
                    </span>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CUM FUNCȚIONEAZĂ ═══ */}
      <section style={sectionPadding}>
        <div style={sectionInner}>
          <AnimatedSection>
            <h2 style={sectionTitle}>
              <span style={{ color: '#44475a' }}>$ </span>
              <span style={{ color: '#50fa7b' }}>Cum funcționează</span>
            </h2>
            <p style={sectionSubtitle}>3 pași simpli de la descriere la website funcțional. AI-ul nostru face magia.</p>
          </AnimatedSection>

          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={0.15 * i}>
                <div style={{
                  background: '#13141a', border: '1px solid #1e2030',
                  borderRadius: 16, padding: '36px 28px', textAlign: 'center',
                  position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: 120, height: 2,
                    background: `linear-gradient(90deg, transparent, ${step.color}, transparent)`,
                  }} />
                  <div style={{
                    fontSize: 11, color: step.color, fontWeight: 600,
                    fontFamily: "'JetBrains Mono', monospace",
                    marginBottom: 16, letterSpacing: 2,
                  }}>PASUL {step.num}</div>
                  <div style={{ fontSize: 44, marginBottom: 16, animation: `float 3s ease-in-out ${i * 0.5}s infinite` }}>{step.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, fontFamily: "'Space Grotesk', sans-serif", color: '#f8f8f2' }}>{step.title}</h3>
                  <p style={{ color: '#6272a4', fontSize: 13, lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AI DEMO SHOWCASE ═══ */}
      <section style={{ ...sectionPadding, background: `radial-gradient(ellipse at 50% 50%, rgba(255,121,198,0.04) 0%, transparent 60%)` }}>
        <div style={{ ...sectionInner, maxWidth: 800 }}>
          <AnimatedSection>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🧄</div>
              <h2 style={{
                ...sectionTitle,
                background: 'linear-gradient(135deg, #ff79c6, #bd93f9)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                display: 'inline-block',
              }}>
                Ai-ul nostru din Transilvania
              </h2>
              <p style={{ ...sectionSubtitle, marginBottom: 40 }}>
                Generăm 3 variante de website demo gratuit, personalizate pentru afacerea ta. Fără obligații, fără costuri ascunse. 30 de secunde, 3 stiluri diferite.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 40 }}>
              {[
                { label: 'Modern & Minimalist', color: '#50fa7b', icon: '🎨', desc: 'Clean, airy, sans-serif' },
                { label: 'Bold & Colorful', color: '#ff79c6', icon: '🎆', desc: 'Gradient-uri, animații' },
                { label: 'Classic & Professional', color: '#bd93f9', icon: '🏛️', desc: 'Elegant, serif, warm' },
              ].map((demo, i) => (
                <div key={i} style={{
                  background: '#13141a', border: `1px solid ${demo.color}20`,
                  borderRadius: 12, padding: '24px 20px', textAlign: 'center', transition: 'all 0.3s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = demo.color + '60'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = demo.color + '20'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{demo.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: demo.color, marginBottom: 6, fontFamily: "'Space Grotesk', sans-serif" }}>{demo.label}</div>
                  <div style={{ fontSize: 11, color: '#44475a', fontFamily: "'JetBrains Mono', monospace" }}>{demo.desc}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div style={{ textAlign: 'center' }}>
              <button onClick={onOpenForm} style={{
                background: 'linear-gradient(135deg, #ff79c6, #bd93f9)',
                border: 'none', borderRadius: 10, padding: '15px 40px',
                color: '#fff', fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
                boxShadow: '0 0 30px rgba(255,121,198,0.2)', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}>
                🧛 Generează Demo Gratuit →
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ DE CE DRACULADEV ═══ */}
      <section style={sectionPadding}>
        <div style={sectionInner}>
          <AnimatedSection>
            <h2 style={sectionTitle}>
              <span style={{ color: '#44475a' }}># </span>
              <span style={{ color: '#8be9fd' }}>De ce DraculaDev?</span>
            </h2>
            <p style={sectionSubtitle}>Nu suntem o corporație fără chip. Suntem dezvoltatori care vorbesc limba ta.</p>
          </AnimatedSection>

          <div className="trust-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {trustPoints.map((point, i) => (
              <AnimatedSection key={i} delay={0.08 * i}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  background: '#13141a', border: '1px solid #1e2030',
                  borderRadius: 12, padding: '18px 22px', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#8be9fd30'; e.currentTarget.style.background = '#8be9fd05'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2030'; e.currentTarget.style.background = '#13141a'; }}>
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{point.icon}</span>
                  <span style={{ color: '#a0a4b8', fontSize: 14, fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" }}>{point.text}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PREȚURI ═══ */}
      <section id="preturi" style={{ ...sectionPadding, background: `radial-gradient(ellipse at 50% 30%, rgba(80,250,123,0.03) 0%, transparent 50%)` }}>
        <div style={sectionInner}>
          <AnimatedSection>
            <h2 style={sectionTitle}>
              <span style={{ color: '#44475a' }}>{'>'} </span>
              <span style={{ color: '#f1fa8c' }}>Prețuri orientative</span>
            </h2>
            <p style={sectionSubtitle}>Transparent, fără surprize. Prețuri de la, personalizate în funcție de complexitate.</p>
          </AnimatedSection>

          <div className="prices-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            {prices.map((p, i) => (
              <AnimatedSection key={i} delay={0.1 * i}>
                <div
                  onMouseEnter={() => setHoveredPrice(i)}
                  onMouseLeave={() => setHoveredPrice(null)}
                  style={{
                    background: hoveredPrice === i ? `${p.color}06` : '#13141a',
                    border: `1px solid ${p.popular ? p.color + '40' : (hoveredPrice === i ? p.color + '30' : '#1e2030')}`,
                    borderRadius: 14, padding: '28px 22px', textAlign: 'center',
                    transition: 'all 0.3s', position: 'relative',
                    transform: hoveredPrice === i ? 'translateY(-4px)' : 'none',
                  }}>
                  {p.popular && (
                    <div style={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      background: p.color, color: '#0d0e12', fontSize: 10, fontWeight: 700,
                      padding: '3px 14px', borderRadius: 10, fontFamily: "'JetBrains Mono', monospace",
                    }}>POPULAR</div>
                  )}
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{p.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#f8f8f2', marginBottom: 8, fontFamily: "'Space Grotesk', sans-serif" }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: '#44475a', marginBottom: 16, fontFamily: "'JetBrains Mono', monospace" }}>{p.desc}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: '#6272a4' }}>de la</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: p.color, fontFamily: "'Space Grotesk', sans-serif" }}>{p.price}</span>
                    <span style={{ fontSize: 11, color: '#6272a4', fontFamily: "'JetBrains Mono', monospace" }}>{p.unit}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA FINAL ═══ */}
      <section style={{ ...sectionPadding, paddingBottom: 60 }}>
        <AnimatedSection>
          <div style={{
            background: 'linear-gradient(135deg, rgba(189,147,249,0.08), rgba(255,121,198,0.06))',
            border: '1px solid rgba(189,147,249,0.15)',
            borderRadius: 20, padding: '56px 32px', maxWidth: 620, margin: '0 auto',
            textAlign: 'center', animation: 'pulseGlow 5s ease-in-out infinite',
          }}>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, marginBottom: 12, fontWeight: 700 }}>
              Ai o afacere fără website?
            </h2>
            <p style={{ color: '#6272a4', marginBottom: 28, fontSize: 15, lineHeight: 1.6 }}>
              Spune-ne despre afacerea ta și primești 3 variante demo gratuit.<br />Fără obligații, fără colți ascunși. 🧛
            </p>
            <button onClick={onOpenForm} style={{
              background: 'linear-gradient(135deg, #ff79c6, #bd93f9)',
              border: 'none', borderRadius: 10, padding: '15px 36px',
              color: '#fff', fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700, fontSize: 15, cursor: 'pointer',
              boxShadow: '0 0 25px rgba(255,121,198,0.2)', transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}>
              🦇 Descrie afacerea ta →
            </button>
          </div>
        </AnimatedSection>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ position: 'relative', zIndex: 10 }}>
        <CastleSilhouette color="rgba(10,11,15,0.95)" />
        <div style={{ background: 'rgba(10,11,15,0.95)', padding: '40px 24px 24px', borderTop: '1px solid #1a1c28' }}>
          <div style={{
            maxWidth: 1000, margin: '0 auto',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: 32,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 22 }}>🧛</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#f8f8f2', fontFamily: "'Space Grotesk', sans-serif" }}>DraculaDev</span>
              </div>
              <div style={{ color: '#44475a', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6 }}>
                contact@draculadev.com<br />Forged in Transylvania 🏔️
              </div>
            </div>
            <div>
              <div style={{ color: '#6272a4', fontSize: 11, fontWeight: 600, marginBottom: 10, letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>SERVICII</div>
              {services.map((s, i) => (
                <Link key={i} to={`/servicii/${s.slug}`} style={{
                  display: 'block', color: '#44475a', textDecoration: 'none',
                  fontSize: 12, marginBottom: 6, transition: 'color 0.2s',
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
                onMouseEnter={e => e.target.style.color = s.color}
                onMouseLeave={e => e.target.style.color = '#44475a'}>
                  {s.icon} {s.title}
                </Link>
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1a1c28', marginTop: 32, paddingTop: 16, textAlign: 'center' }}>
            <span style={{ color: '#2a2d3a', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              © 2026 DraculaDev.com — Forged in Transylvania 🧛
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PresentationPage;